/**
 * Detect "in-app" browsers (Telegram, WhatsApp, Instagram, Facebook, etc.)
 * that have restrictive behavior around cookies, service workers, and
 * web push. We surface a banner to the user pointing them to open the
 * link in their real browser (Chrome / Safari).
 */

const PATTERNS = [
  { id: "telegram",  rx: /TelegramAndroid|Telegram\/|tgWeb/i,  label: "Telegram" },
  { id: "whatsapp",  rx: /WhatsApp/i,                          label: "WhatsApp" },
  { id: "instagram", rx: /Instagram/i,                         label: "Instagram" },
  { id: "facebook",  rx: /FBAN|FBAV|FB_IAB/i,                  label: "Facebook" },
  { id: "messenger", rx: /Messenger/i,                         label: "Messenger" },
  { id: "tiktok",    rx: /Bytedance|TikTok/i,                  label: "TikTok" },
  { id: "line",      rx: /Line\//i,                            label: "LINE" },
];

export function detectInAppBrowser() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  for (const p of PATTERNS) {
    if (p.rx.test(ua)) return { id: p.id, label: p.label, ua };
  }
  // Heuristic: many in-app browsers expose `window.TelegramWebviewProxy`
  if (typeof window !== "undefined" && window.TelegramWebviewProxy) {
    return { id: "telegram", label: "Telegram", ua };
  }
  return null;
}

export function isInAppBrowser() {
  return detectInAppBrowser() !== null;
}

/**
 * Try to bounce the user out of the in-app webview into their real browser.
 * Best-effort: each platform exposes a different mechanism, and some
 * (Instagram in particular) actively block this.
 */
export function escapeToBrowser(url = window.location.href) {
  const info = detectInAppBrowser();
  if (!info) return false;

  // Android Chrome intent fallback works for most Android in-app browsers
  const isAndroid = /android/i.test(navigator.userAgent || "");
  if (isAndroid) {
    const u = new URL(url);
    const intent = `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=${u.protocol.replace(":", "")};package=com.android.chrome;end`;
    window.location.href = intent;
    return true;
  }

  // iOS: x-safari- scheme works on most builds
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || "");
  if (isIOS) {
    window.location.href = url.replace(/^https?:\/\//, "x-safari-https://");
    return true;
  }

  // Fallback: just open in a new tab — the webview will usually refuse
  // and pop the system browser instead.
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}
